/**
 * `error_code` → Arabic copy. Feature-local deltas live in
 * `features/**\/errors.ts`; a backend code inlined in JSX is a defect.
 *
 * Three rules the entries below obey:
 *   • NO STATUS CODE and NO CORRELATION ID are ever printed on a user
 *     surface. Both go to the log line and to telemetry.
 *   • Every message states the FIX, not the blame.
 *   • A failure that originates in an external system is `dependency: true`
 *     and renders NEUTRAL, not alarming red — it is not the reader's mistake.
 */
import { ApiError } from '@shared/api/envelope';

export interface ErrorCopy {
  /** Sentence shown to the reader. Never a code, never a status number. */
  message: string;
  /** Dependency failures take neutral styling and a manual retry. */
  dependency?: boolean;
}

const FALLBACK: ErrorCopy = {
  message: 'تعذّر إتمام الطلب. أعد المحاولة، وإن تكرّر الأمر فأبلغ الدعم.',
};

export const errorCatalog: Readonly<Record<string, ErrorCopy>> = {
  // ---- client-side, never from a backend ------------------------------------
  CLIENT_NETWORK_UNREACHABLE: { message: 'لا يوجد اتصال بالشبكة الآن.', dependency: true },
  CLIENT_ABORTED: { message: 'أُلغي الطلب.' },
  CLIENT_MALFORMED_RESPONSE: { message: 'وصل ردّ غير متوقّع. أعد المحاولة.', dependency: true },
  CLIENT_UNKNOWN_ERROR: FALLBACK,

  // ---- backend-auth ---------------------------------------------------------
  AUTH_PHONE_INVALID: { message: 'رقم الهاتف غير مكتمل.' },
  AUTH_OTP_INVALID: { message: 'الرمز غير صحيح. اطلب رمزاً جديداً أو أعد إدخاله.' },
  AUTH_OTP_EXPIRED: { message: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً.' },
  // NOT «the previous code is still valid», and NOT a second key beside it.
  //
  // `backend-auth` REPLACES a live code on every request — purge-then-insert in
  // one transaction, so the newest digits win and the old ones stop verifying.
  // Nothing is ever waiting to expire, and `AUTH_OTP_RESEND_TOO_SOON` was a key
  // no service emits, so its copy was unreachable either way.
  //
  // The one 429 this path can raise is the per-number SPEND cap
  // (`OTP_REQUEST_PER_IDENTIFIER_LIMIT` counted over
  // `LOGIN_LOCKOUT_WINDOW_SECONDS`), because a code request costs the operator
  // money at the provider. Naming the cap is what makes the wait actionable —
  // the old copy sent the reader to watch a code expire that had already been
  // replaced, so waiting for it could not end the bar.
  AUTH_OTP_RESEND_BARRED: {
    message: 'طلبات كثيرة على هذا الرقم خلال وقت قصير. انتظر قليلاً ثمّ اطلب رمزاً جديداً.',
  },
  AUTH_LOGIN_LOCKED: { message: 'أُوقف الدخول مؤقّتاً بعد محاولات متكرّرة. انتظر قليلاً ثمّ أعد المحاولة.' },
  AUTH_RATE_LIMITED: { message: 'محاولات كثيرة خلال وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.' },
  AUTH_METHOD_NOT_ENABLED: { message: 'طريقة الدخول هذه غير مفعّلة على المنصّة.' },
  AUTH_PASSWORD_NOT_SET: {
    message: 'لا توجد كلمة مرور على هذا الحساب. ادخل برمز التحقّق ثمّ عيّن واحدة من الإعدادات.',
  },
  AUTH_USER_BLOCKED: { message: 'هذا الحساب موقوف. راسل الدعم من صفحة اتصل بنا.' },
  AUTH_ORIGIN_NOT_PERMITTED: { message: 'تعذّر إتمام الطلب من هذا العنوان.', dependency: true },
  AUTH_SMS_DISPATCH_FAILED: { message: 'تعذّر إرسال الرمز الآن. أعد المحاولة بعد قليل.', dependency: true },
  AUTH_UPSTREAM_TIMEOUT: { message: 'الخدمة مشغولة الآن. أعد المحاولة بعد قليل.', dependency: true },
  // A replay must not be distinguishable from an ordinary expiry. These two
  // carry the same copy BY DESIGN — do not separate them.
  AUTH_SESSION_REVOKED: { message: 'انتهت الجلسة. سجّل الدخول من جديد.' },
  AUTH_REFRESH_REPLAYED: { message: 'انتهت الجلسة. سجّل الدخول من جديد.' },
  AUTH_SESSION_ENDED: { message: 'انتهت الجلسة. سجّل الدخول من جديد.' },
  AUTH_BIOGRAPHY_NOT_APPLICABLE: { message: 'النبذة متاحة للحسابات التي تحمل دوراً فقط.' },

  // ---- backend-content ------------------------------------------------------
  CONTENT_ITEM_NOT_FOUND: { message: 'لم يعد هذا العنوان متاحاً.' },
  CONTENT_SHELF_SOURCE_NOT_CONFIGURED: { message: 'تعذّر عرض هذا الرفّ.' },
  CONTENT_COLLECTION_NOT_FOUND: { message: 'لم تعد هذه القائمة متاحة.' },

  // ---- backend-assets -------------------------------------------------------
  ASSETS_NOT_ENTITLED: { message: 'اشتراكك لا يغطّي هذا العنوان الآن. راجع خطط الاشتراك.' },
  ASSETS_RENDITION_NOT_READY: { message: 'ما زال العمل قيد التجهيز للعرض. سيبدأ التشغيل حال جاهزيته.' },
  ASSETS_DELIVERY_MODE_FORBIDDEN: { message: 'هذا العمل يُشاهَد عبر البثّ فقط.' },

  // ---- backend-finance ------------------------------------------------------
  FINANCE_ALREADY_SUBSCRIBED: { message: 'لديك اشتراك فعّال.' },
  FINANCE_PLAN_NOT_AVAILABLE: { message: 'لم تعد هذه الخطّة متاحة. اختر خطّة من القائمة المحدَّثة.' },
  FINANCE_NO_ACTIVE_GATEWAY: { message: 'الدفع غير متاح الآن. راسل الدعم من صفحة اتصل بنا.' },
  FINANCE_GATEWAY_DISABLED: { message: 'الدفع غير متاح الآن. راسل الدعم من صفحة اتصل بنا.' },
  FINANCE_SETTLEMENT_FAILED: { message: 'لم تتمّ عملية الدفع، ولم يُخصم أيّ مبلغ ولم يُفعَّل أيّ اشتراك.' },
  FINANCE_PSP_UNAVAILABLE: { message: 'خدمة الدفع مشغولة الآن. أعد المحاولة بعد قليل.', dependency: true },
  FINANCE_PSP_TIMEOUT: {
    message: 'خدمة الدفع تأخّرت في الردّ. تحقّق من حالة اشتراكك قبل إعادة المحاولة.',
    dependency: true,
  },
  FINANCE_RATE_LIMITED: { message: 'محاولات كثيرة خلال وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.' },

  // ---- backend-engagement ---------------------------------------------------
  ENGAGEMENT_COMMENT_PENDING_MODERATION: { message: 'وصل تعليقك وهو بانتظار المراجعة قبل نشره.' },

  // ---- backend-notification -------------------------------------------------
  NOTIFICATION_BULK_LIMIT_EXCEEDED: { message: 'العدد المحدَّد أكبر ممّا يقبله الإجراء دفعة واحدة.' },
  NOTIFICATION_SORT_COLUMN_NOT_ALLOWED: { message: 'أُعيد الترتيب إلى الوضع المعتمد.' },

  // ---- backend-support ------------------------------------------------------
  SUPPORT_CASE_REASON_UNKNOWN: { message: 'تغيّرت قائمة الأسباب. اختر سبباً من القائمة المحدَّثة.' },
  SUPPORT_CASE_SUBJECT_REQUIRED: { message: 'هذا النوع من الحالات يُفتح من صفحة الموضوع نفسه.' },
  SUPPORT_CASE_SUBJECT_FORBIDDEN: { message: 'هذا النوع من الحالات لا يُرفق بموضوع.' },
  SUPPORT_CASE_SUBJECT_NOT_FOUND: { message: 'لم يعد الموضوع المرتبط بهذه الحالة متاحاً.' },
  SUPPORT_CASE_CLOSED: { message: 'أُغلقت هذه الحالة. افتح حالة جديدة وأشر إلى رقمها.' },
  SUPPORT_CASE_STATE_UNKNOWN: { message: 'تعذّر عرض حالة هذه الطلبيّة الآن.' },

  // ---- backend-search -------------------------------------------------------
  /**
   * The service refuses a one-letter term, and this catalogue had no entry for
   * the refusal — so a subscriber who typed «ا» and pressed «ابحث» was told
   * «تعذّر إتمام الطلب. أعد المحاولة» and, if it kept happening, to send the
   * request number to support. Retrying and reporting were both useless: the
   * answer was one more letter, and the public site had been saying so all
   * along. A generic failure over a refusal the reader can act on is the worst
   * of the two, because it hides an action behind an incident.
   */
  SEARCH_QUERY_TOO_SHORT: { message: 'كلمة البحث قصيرة جداً. اكتب حرفين على الأقل.' },
  SEARCH_CURSOR_INVALID: { message: 'تغيّرت النتائج أثناء التصفّح. أُعيد عرضها من البداية.' },
  SEARCH_FILTER_INVALID: { message: 'أحد المرشِّحات لم يعد متاحاً. امسح المرشِّحات وأعد المحاولة.' },
  SEARCH_FILTER_COLUMN_NOT_ALLOWED: {
    message: 'أحد المرشِّحات لم يعد متاحاً. امسح المرشِّحات وأعد المحاولة.',
  },
  SEARCH_FACET_NOT_CONFIGURED: {
    message: 'أحد المرشِّحات لم يعد ضمن الخيارات. امسح المرشِّحات وأعد المحاولة.',
  },
  SEARCH_YEAR_RANGE_INVALID: { message: 'مدى السنوات غير صالح. اجعل «من» قبل «إلى».' },
};

export function lookupErrorCopy(errorCode: string): ErrorCopy {
  return errorCatalog[errorCode] ?? FALLBACK;
}

/** The one place a thrown value becomes reader-facing copy. */
export function describeError(error: unknown): ErrorCopy {
  if (error instanceof ApiError) {
    return lookupErrorCopy(error.errorCode);
  }
  return FALLBACK;
}
